import React, { useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { Formik, Form, Field } from "formik";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../Appstore/store";
import {
  UpdateSmsCampaignsShortcuts,
  GetallSmsCampaignShortcuts,
} from "../Features/SmscampaignShortcuts/Smscampaignshortcuts";
import * as Yup from "yup";
import { setIsLoading } from "../Features/loadingSlice";
import { toast } from "sonner";

interface UpdatesmscampaignshortcutsmodalProps {
  show: boolean;
  onHide: () => void;
  record: any;
  id: number | null;
}

interface FormValues {
  code: string;
  message: string;
}

const validationSchema = Yup.object({
  code: Yup.string()
    .required("Code is required")
    .trim()
    .matches(
      /^[^#]+$/,
      "Code cannot contain the # symbol and # symbol is your start with by default set"
    )
    .matches(/^\S+$/, "Code cannot contain spaces"),

  message: Yup.string().required("Message is required").trim(),
});

const Updatesmscampaignshortcuts: React.FC<
  UpdatesmscampaignshortcutsmodalProps
> = ({ show, onHide, record, id }) => {
  const dispatch = useDispatch<AppDispatch>();

  const { error, smscampaignshortcutsdata, loading } = useSelector(
    (state: RootState) => state.smscampaignshortcuts
  );

  const beforedata = smscampaignshortcutsdata.find((item) => item.id === id);

  useEffect(() => {
    if (show) {
      dispatch(setIsLoading(true));
      setTimeout(() => {
        dispatch(setIsLoading(false));
      }, 500);
    }
  }, [show, dispatch]);

  const initialValues = {
    code: beforedata ? beforedata.code.replace(/^#/, "") : "",
    message: beforedata ? beforedata.message : "",
  };

  return (
    <>
      <Modal show={show} onHide={onHide} centered>
        <Modal.Header closeButton className="border-0">
          <Modal.Title>Edit Sms Campaign Shortcuts</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={async (values, { resetForm }) => {
              const updateData = {
                code: values.code?.startsWith("#")
                  ? values.code
                  : `#${values.code ?? ""}`,
                message: values.message,
                sms_campaign_id: record.id,
              };
              try {
                const response: any = await dispatch(
                  UpdateSmsCampaignsShortcuts({ id: id ?? 0, updateData })
                );

                if (response.payload && response.payload.status === "success") {
                  toast.success(response.payload.message);
                  resetForm();
                  onHide();
                  await dispatch(
                    GetallSmsCampaignShortcuts({
                      search: "",
                      page: 1,
                      limit: 10,
                      sms_campaign_id: record.id,
                    })
                  );
                } else {
                  toast.error(response.payload.message);
                }
              } catch (error: any) {
                const errorMessage =
                  error.message || "An unexpected error occurred";
                toast.error(errorMessage);
              }
            }}
          >
            {({ errors, touched, handleChange, values, isSubmitting }) => (
              <Form>
                <div className="form-group mb-3">
                  <label htmlFor="code">
                    Code<span className="text-danger">*</span>
                  </label>
                  <div className="input-group">
                    <div className="input-group-prepend">
                      <span className="input-group-text">#</span>
                    </div>
                    <Field
                      type="text"
                      className={`form-control shadow-none ${
                        touched.code && errors.code
                          ? "is-invalid rounded-end-2"
                          : ""
                      }`}
                      id="code"
                      name="code"
                      placeholder="Enter a Code"
                    />
                    {touched.code && errors.code ? (
                      <div className="invalid-feedback">{errors.code}</div>
                    ) : null}
                  </div>
                </div>

                <div className="form-group mb-3">
                  <label htmlFor="message">
                    Message<span className="text-danger">*</span>
                  </label>
                  <Field
                    as="textarea"
                    className={`form-control shadow-none ${
                      touched.message && errors.message ? "is-invalid" : ""
                    }`}
                    id="message"
                    name="message"
                    placeholder="Enter a Message"
                    rows="4"
                  />
                  {touched.message && errors.message ? (
                    <div className="invalid-feedback">{errors.message}</div>
                  ) : null}
                </div>

                <Modal.Footer className="d-flex justify-content-center border-0">
                  <Button
                    style={{ width: 100 }}
                    variant="primary"
                    type="submit"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span className="ps-2">Saving...</span>
                      </>
                    ) : (
                      "Save"
                    )}
                  </Button>
                  <Button
                    style={{ width: 100 }}
                    variant="secondary"
                    onClick={onHide}
                  >
                    Close
                  </Button>
                </Modal.Footer>
              </Form>
            )}
          </Formik>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default Updatesmscampaignshortcuts;
